import type { AnimationI, AnySceneObjectT, SimpleStyleT, TweenT } from "tweenly"
import { v4 as uuidv4 } from "uuid"
import { defaultEase } from "../data/styles/ease.styles"
import { sequence } from "../data/styles/tweens.styles"
import { FileResponse, get } from "../helpers/ajax.helpers"
import { createAnimation } from "tweenly"
import { S3_URL } from "../helpers/file.helpers"
import { framesToSeconds } from "../helpers/framerate.helpers"
import {
    addSelectedAnimationsAction,
    clearSelectedAnimationsAction,
    removeSelectedAnimationsAction,
} from "../store/slices/activeAnimation.slice"
import { addObjectToSelectedObjectsAction } from "../store/slices/activeObject.slice"
import { AssetT } from "../store/slices/assets.slice"
import { updateMasterTimeline } from "../store/slices/masterTimeline.slice"
import {
    addAnimationAction,
    createSequenceAnimationsAction,
    deleteAnimationsAction,
    duplicateAnimationsAction,
    updateObjectAnimationAction,
} from "../store/slices/objects.slice"
import { useAppDispatch, useAppStore } from "./useRedux"

export const useAnimationActions = () => {
    const dispatch = useAppDispatch()
    // read on demand, never subscribed — see useTimelineActions
    const store = useAppStore()
    const getTimeToSeek = () => store.getState().masterTimeline.time
    // `useFramerate` would subscribe to timelinePreferences — this hook is pulled in by
    // `useObjectActions`, i.e. once per canvas object
    const getFramerate = () => store.getState().timelinePreferences.value.framerate

    /* ACTIVE ANIMATION HANDLERS */

    const setActiveAnimations = (ids: string[], objectId: string) => {
        dispatch(addSelectedAnimationsAction(ids))
        dispatch(addObjectToSelectedObjectsAction({ id: objectId }))
    }

    const addSelectedAnimations = (ids: string[]) => {
        dispatch(addSelectedAnimationsAction(ids))
    }

    const removeSelectedAnimations = (ids: string[]) => {
        dispatch(removeSelectedAnimationsAction(ids))
    }

    /* HELPER HANDLERS */

    const handleActiveObjectWhenCreating = (id: string, objectId: string) => {
        dispatch(clearSelectedAnimationsAction())
        setActiveAnimations([id], objectId)
    }

    const addAnimation = (animation: AnimationI) => {
        dispatch(addAnimationAction({ animation: animation, objectId: animation.objectId }))
        handleActiveObjectWhenCreating(animation.id, animation.objectId)

        dispatch(updateMasterTimeline(getTimeToSeek()))
    }

    const buildSequenceAnimations = async (asset: AssetT, objectId: string) => {
        const step = framesToSeconds(getFramerate(), 1)

        let urls = asset.urls
        if (!urls) {
            const files = await get<FileResponse[]>("files", {
                key: `sequences/${asset.slug}`,
            })
            urls = files.map((file) => {
                const key = file.filename.replace(".", `.${file._id}.`)
                return `${S3_URL}/${key}`
            })
        }

        const animations = urls.map((url, index) => {
            const [name, , extension] = (url.split("/").pop() ?? "").split(".")
            const animation: AnimationI = {
                id: uuidv4(),
                target: null,
                title: `${name}.${extension}`,
                objectId,
                duration: step,
                delay: index * step,
                repeat: 0,
                tween: sequence(url),
                relativeTo: null,
                ease: defaultEase,
                onInitCode: null,
                onUpdateCode: null,
                subTimeline: false,
            }
            return animation
        })

        return { urls, animations }
    }

    const createSequenceAnimations = (
        animations: AnimationI[],
        object: AnySceneObjectT,
        dimensions: { width: number; height: number }
    ) => {
        dispatch(createSequenceAnimationsAction({ animations, object, dimensions }))
        setTimeout(() => dispatch(updateMasterTimeline(getTimeToSeek())), 500) // short delay to ensure that the objects are created in Canvas
    }
    const addAnimations = (animations: AnimationI[]) => {
        animations.forEach((animation: AnimationI) => {
            //breakpoint
            dispatch(addAnimationAction({ animation: animation, objectId: animation.objectId }))
            // handleAnimationIdsWhenCreating(animation.objectId, animation.id)
        })

        setTimeout(() => dispatch(updateMasterTimeline(getTimeToSeek())), 500) // short delay to ensure that the objects are created in Canvas
    }

    const addNewAnimation = (objectId: string, startOffset: number) =>
        addAnimation(createAnimation(objectId, startOffset, undefined))

    const duplicateAnimations = (animations: AnimationI[]) => {
        let temporaryDuplicatedAnimation: AnimationI[] = []

        animations.forEach((animation: AnimationI) => {
            temporaryDuplicatedAnimation.push(
                createAnimation(animation.objectId, animation.delay, {
                    ...animation,
                    title: animation.title + " copy",
                })
            )
        })
        dispatch(duplicateAnimationsAction({ animations: temporaryDuplicatedAnimation }))
        dispatch(clearSelectedAnimationsAction())
        temporaryDuplicatedAnimation.forEach((newAnimation) => {
            dispatch(addSelectedAnimationsAction([newAnimation.id]))
            dispatch(addObjectToSelectedObjectsAction({ id: newAnimation.objectId }))
        })
    }
    const handleRefresh = () => {
        dispatch(updateMasterTimeline(getTimeToSeek()))
    }

    const updateAnimation = (id: string, objectId: string, property: any, value: any) => {
        dispatch(
            updateObjectAnimationAction(
                {
                    id: id,
                    objectId: objectId,
                    property: property,
                    value: value,
                },
                handleRefresh
            )
        )
    }

    const updateAnimationTweenState = (
        animation: AnimationI,
        state: "from" | "to",
        property: string,
        value: any
    ) => {
        const updatedStateObject: SimpleStyleT[] = animation.tween[state].map((stateObject) => {
            if (stateObject.property === property) return { ...stateObject, value: value }
            else return stateObject
        })

        const updatedTween: TweenT = {
            ...animation.tween,
            [state]: updatedStateObject,
        }
        updateAnimation(animation.id, animation.objectId, "tween", updatedTween)
    }

    /**
     * delete selected animations and clear activeAnimations
     * @param animations
     */
    const deleteAnimations = (animations: AnimationI[]) => {
        dispatch(deleteAnimationsAction({ animations }))

        //clear state.activeAnimation (value, selected) //to do -refactor only keep selected
        dispatch(clearSelectedAnimationsAction())

        //refresh styles and set timeToSeek
        dispatch(updateMasterTimeline(getTimeToSeek()))
    }

    return {
        addNewAnimation,
        addAnimation,
        addAnimations,
        createSequenceAnimations,
        duplicateAnimations,
        updateAnimation,
        updateAnimationTweenState,
        deleteAnimations,
        setActiveAnimations,
        addSelectedAnimations,
        removeSelectedAnimations,
        buildSequenceAnimations,
    }
}
